import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FileInputComponent, { FileInputHandle } from 'components/shared/fileInput/FileInputComponent';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import Group from 'models/Group';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, createAdmission } from 'services/api/admissions';
import { GroupAccessMode, useInfiniteGroups } from 'services/api/group';
import { AIJobNotificationType, TrackedJob } from 'stores/aiJobNotificationStore';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import ClipIcon from 'svg/AttachFileIcon';

import usePrefetchGroups from './hooks/usePrefetchGroups';

export default function AdmissionsUploadSection() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { startTracking } = useTrackAIJob();
  const { groupParams: snfGroupParams, query: snfQuery } = usePrefetchGroups(SKILLED_NURSING_FACILITY, {
    clientId: profile.actingClient.id,
    accessModes: [GroupAccessMode.Allowed],
  });
  const singleGroup = snfQuery.data?.meta.totalRecords === 1 ? snfQuery.data.data[0] : null;
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showSnfModal, setShowSnfModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const fileInputRef = useRef<FileInputHandle>(null);

  const { options: groupOptions, onDebouncedSearch } = useInfiniteGroups(snfGroupParams);

  const { mutate: submitAdmission, isPending: isSubmitting } = useMutateWithToast({
    mutationFn: (data: { attachments: Upload[]; groupId: string }) => createAdmission(data),
    onSuccess: (admission: Admission) => {
      fileInputRef.current?.reset();
      setShowSnfModal(false);
      setSelectedGroup(null);
      queryClient.invalidateQueries({ queryKey: admissionQueryKeys.base });

      const jobs = [
        admission.summaryJobForCurrentAttachments
          ? { id: admission.summaryJobForCurrentAttachments.id, type: AIJobType.SUMMARY }
          : null,
        admission.criteriaEvaluationJob
          ? { id: admission.criteriaEvaluationJob.id, type: AIJobType.CRITERIA_EVALUATION }
          : null,
      ].filter((j): j is TrackedJob => j !== null);

      if (jobs.length > 0) {
        startTracking({
          jobs,
          jobType: AIJobNotificationType.ADMISSION_SUMMARY,
          jobbableId: admission.id,
          returnUrl: `/admissions/${admission.id}/criteria`,
        });
      }

      navigate(`/admissions/${admission.id}/criteria`);
    },
  });

  const handleStartAdmission = () => {
    if (singleGroup) {
      const attachments = uploads.filter((u) => u.signedId);
      if (attachments.length === 0) return;
      submitAdmission({ attachments, groupId: singleGroup.id });
      return;
    }
    setShowSnfModal(true);
  };

  const handleConfirm = () => {
    const attachments = uploads.filter((u) => u.signedId);
    if (attachments.length === 0 || !selectedGroup) return;
    submitAdmission({ attachments, groupId: selectedGroup.id });
  };

  const handleCancelModal = () => {
    setShowSnfModal(false);
    setSelectedGroup(null);
  };

  const reset = () => {
    fileInputRef.current?.reset();
  };

  const completedUploads = uploads.every((u) => u.isComplete);
  const canSubmit = uploads.length > 0 && completedUploads && !isUploading && !isSubmitting;

  return (
    <Container>
      <FileInputComponent
        ref={fileInputRef}
        onChange={setUploads}
        onUploading={setIsUploading}
        uploadList='inside'
        hideLabel
        containerStyle={{ height: 160 }}
        draggingStyles={{
          backgroundColor: colors.blue5,
          borderColor: colors.blue100,
        }}
        Icon={() => (
          <UploadHeader>
            <UploadIconWrapper>
              <ClipIcon height={16} />
            </UploadIconWrapper>
            <UploadTitle>Upload New Referral Packet</UploadTitle>
            <UploadSubtitle>
              To start a new admission summary, drag and drop a referral packet here, or{' '}
              <BrowseLink>browse your files</BrowseLink>
            </UploadSubtitle>
          </UploadHeader>
        )}
      />
      {uploads.length > 0 && (
        <ActionBar>
          <Button variant='ghost' onClick={reset}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleStartAdmission} disabled={!canSubmit}>
            Start Admission Summary
          </Button>
        </ActionBar>
      )}
      {showSnfModal && (
        <StandardModal title='Select SNF' onCancel={handleCancelModal} closeOnBackdropClick>
          <ModalBody>
            <ModalDescription>Select which SNF this patient is being referred to below</ModalDescription>
            <FieldLabel>SNF</FieldLabel>
            <Select<Group>
              options={groupOptions}
              getOptionValue={(g) => g.id}
              getOptionLabel={(g) => g.name}
              placeholder='Select SNF'
              onChange={setSelectedGroup}
              value={selectedGroup}
              onDebouncedSearch={onDebouncedSearch}
            />
          </ModalBody>
          <StyledButtonGroup>
            <Button variant='ghost' onClick={handleCancelModal}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedGroup || isSubmitting} loading={isSubmitting}>
              Confirm
            </Button>
          </StyledButtonGroup>
        </StandardModal>
      )}
    </Container>
  );
}

const Container = styled.div`
  margin: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadIconWrapper = styled.div`
  color: ${colors.blue100};
  line-height: 0;
`;

const UploadHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
`;

const UploadTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const UploadSubtitle = styled.span`
  font-size: 12px;
  color: ${colors.black75};
  text-align: center;
`;

const BrowseLink = styled.span`
  color: ${colors.blue100};
  text-decoration: underline;
  cursor: pointer;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const ModalBody = styled.div`
  margin: 16px 0 32px;
`;

const ModalDescription = styled(BodySmall)`
  margin-bottom: 24px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${colors.black75};
`;

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
`;
